import React, {ReactNode, useEffect, useState} from "react";
import {Box, Button, ButtonBase, Stack, Typography} from "@mui/material";
import CakeRoundedIcon from "@mui/icons-material/CakeRounded";
import EuroRoundedIcon from "@mui/icons-material/EuroRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded";
import PriceCheckRoundedIcon from "@mui/icons-material/PriceCheckRounded";
import {
    DocumentRestControllerApiFactory,
    DocumentReviewDto,
    EmployeeStats,
    EmployeeStatsRestControllerApiFactory,
} from "arteco-api-client-ts";
import dayjs from "dayjs";
import {useNavigate} from "react-router-dom";
import {useApiManager} from "../../../components/utils/api";
import {dateFromString, dateToString} from "../../../components/utils/dates";
import {EmptyState, Page, PageHeader, Panel, PersonAvatar, relative, SkeletonRows, SoftChip, Tone} from "../../../components/kit";
import {ReviewStatusChip} from "../compliance/document/review_status";
import {InterviewBoardPanel} from "./interview/board";
import {HrPendingPanel} from "./hr_pending_panel";

/** Una fila de las tarjetas de la portada: lo que identifica a la izquierda y el dato a la derecha. */
const Row = ({left, right, onClick}: { left: ReactNode, right?: ReactNode, onClick: () => void }) => (
    <ButtonBase onClick={onClick} sx={{
        width: "100%", justifyContent: "space-between", gap: 1.5, py: 1, px: 1, mx: -1, borderRadius: 2, textAlign: "left",
        "&:hover": {bgcolor: "action.hover"},
    }}>
        <Box sx={{minWidth: 0, display: "flex", alignItems: "center", gap: 1.5}}>{left}</Box>
        {right && <Box sx={{flexShrink: 0}}>{right}</Box>}
    </ButtonBase>
);

const Person = ({name, caption}: { name?: string, caption?: ReactNode }) => (
    <>
        <PersonAvatar name={name} size={30}/>
        <Box sx={{minWidth: 0}}>
            <Typography variant="body2" fontWeight={600} noWrap>{name}</Typography>
            {caption && <Typography variant="caption" color="text.secondary" component="div" noWrap>{caption}</Typography>}
        </Box>
    </>
);

/**
 * Portada de RR. HH.: los avisos de RR. HH. (#14099), el panel de entrevistas y, debajo, lo que pide atención —cumpleaños, salarios sin
 * revisar desde hace más de un año, documentos de empresa por revisar (#13729) y fines de período de
 * prueba—. Cada fila lleva a la ficha.
 */
export const AdminEmployeePage = () => {
    const navigate = useNavigate();
    const apiManager = useApiManager();
    const [stats, setStats] = useState<EmployeeStats>();
    const [review, setReview] = useState<DocumentReviewDto[]>();

    useEffect(() => {
        apiManager.execute(apiManager.factory(EmployeeStatsRestControllerApiFactory).getEmployeeStats())
            .then(setStats).catch(() => setStats({}));
        apiManager.execute(apiManager.factory(DocumentRestControllerApiFactory).reviewBoardDocument())
            .then(resp => setReview(resp || [])).catch(() => setReview([]));
    }, []);

    const days = (d?: string | null) => d ? dayjs().diff(dayjs(dateFromString(d)), "day") : undefined;
    const person = (id?: number) => () => navigate("/app/admin/employee/person/" + id);

    // Salario: sin revisar en más de un año, aviso; sin salario registrado, error.
    const salaryTone = (d?: string | null): Tone => d ? ((days(d) || 0) > 365 ? "warning" : "neutral") : "error";
    // Período de prueba: a menos de 30 días del fin (o ya pasado), aviso.
    const testTone = (d?: string | null): Tone => d && Math.abs(days(d) || 0) >= 30 ? "neutral" : "warning";

    const birthdays = stats?.nextBirthdays || [];
    const salaries = stats?.pendingSalaries || [];
    const tests = stats?.periodTest || [];

    return (
        <Page>
            <PageHeader title="Personal" subtitle="Entrevistas, revisiones y fechas que piden atención."
                        actions={<Button variant="outlined" startIcon={<PriceCheckRoundedIcon/>}
                                         onClick={() => navigate("/app/admin/employee/rateaudit")}>
                            Desviaciones de tarifa
                        </Button>}/>

            <Stack spacing={3}>
                <Box><HrPendingPanel/></Box>

                <Box><InterviewBoardPanel/></Box>

                <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))"}, gap: 3, alignItems: "start"}}>
                    <Panel title="Próximos cumpleaños" subtitle="Los próximos 30 días" icon={<CakeRoundedIcon fontSize="small" sx={{color: "secondary.main"}}/>}>
                        {!stats ? <SkeletonRows rows={3} height={36}/> : birthdays.length === 0 ? (
                            <EmptyState title="Ningún cumpleaños en los próximos 30 días"/>
                        ) : birthdays.map((b, i) => (
                            <Row key={i} onClick={person(b.personId)} left={<Person name={b.personName}/>}
                                 right={<Typography variant="body2" fontWeight={600}>
                                     {dayjs(new Date(dayjs().year(), (b.month || 1) - 1, b.dayOfMonth)).format("D [de] MMMM")}
                                 </Typography>}/>
                        ))}
                    </Panel>

                    <Panel title="Salarios a revisar" subtitle="Fecha de inicio del último salario; en ámbar si pasa de un año"
                           icon={<EuroRoundedIcon fontSize="small" color="action"/>}>
                        {!stats ? <SkeletonRows rows={3} height={36}/> : salaries.length === 0 ? (
                            <EmptyState title="No hay salarios pendientes de revisar"/>
                        ) : salaries.map((s, i) => (
                            <Row key={i} onClick={person(s.personId)}
                                 left={<Person name={s.personName} caption={s.lastSalary ? "Desde el " + dateToString(s.lastSalary) : "Sin salario registrado"}/>}
                                 right={<SoftChip tone={salaryTone(s.lastSalary)} label={s.lastSalary ? relative(s.lastSalary) : "Sin salario"}/>}/>
                        ))}
                    </Panel>

                    <Panel title="Documentos a revisar" subtitle="Caducados, a 30 días de su revisión o sin revisión configurada"
                           icon={<DescriptionRoundedIcon fontSize="small" color="action"/>}>
                        {!review ? <SkeletonRows rows={3} height={36}/> : review.length === 0 ? (
                            <EmptyState title="Todos los documentos están vigentes"/>
                        ) : review.map(d => (
                            <Row key={d.id} onClick={() => navigate("/app/admin/compliance/document/" + d.id)}
                                 left={<><ReviewStatusChip status={d.status}/><Typography variant="body2" noWrap>{d.title}</Typography></>}
                                 right={<Typography variant="caption" color="text.secondary">{d.nextReviewDate ? dateToString(d.nextReviewDate) : ""}</Typography>}/>
                        ))}
                    </Panel>

                    <Panel title="Períodos de prueba" subtitle="Fin del período de prueba; en ámbar a menos de 30 días"
                           icon={<HourglassBottomRoundedIcon fontSize="small" color="action"/>}>
                        {!stats ? <SkeletonRows rows={3} height={36}/> : tests.length === 0 ? (
                            <EmptyState title="Nadie en período de prueba"/>
                        ) : tests.map((t, i) => (
                            <Row key={i} onClick={person(t.personId)}
                                 left={<Person name={t.personName} caption={t.testPeriod ? "Termina el " + dateToString(t.testPeriod) : undefined}/>}
                                 right={<SoftChip tone={testTone(t.testPeriod)} label={t.testPeriod ? relative(t.testPeriod) : "Sin fecha"}/>}/>
                        ))}
                    </Panel>
                </Box>
            </Stack>
        </Page>
    );
};
